import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { apiQuery, type QueryOut, useApiSuspenseQuery } from "@/client/lib/api";
import type { StaticPath } from "@/client/lib/navigation";
import { ensurePage, pageHead } from "@/client/lib/page";
import { BlocoAgenda2Semanas } from "@/client/modules/inicio/blocos/agenda-2-semanas";
import { BlocoAgendaHoje } from "@/client/modules/inicio/blocos/agenda-hoje";
import { BlocoAtalhos } from "@/client/modules/inicio/blocos/atalhos";
import { renderBlocoEspecialidade } from "@/client/modules/inicio/blocos/especialidades";
import { BlocoGraficos } from "@/client/modules/inicio/blocos/graficos";
import { BlocoKpis } from "@/client/modules/inicio/blocos/kpis";
import { BlocoMural } from "@/client/modules/inicio/blocos/mural";
import { BlocoPendencias } from "@/client/modules/inicio/blocos/pendencias";
import { BlocoSaudacao } from "@/client/modules/inicio/blocos/saudacao";
import Loading from "@/client/modules/inicio/loading";
import { RecepcaoBento } from "@/client/modules/inicio/recepcao-bento";
import type { BlocoHomeKey } from "@/shared/modules/inicio/blocos/registry";

type Search = { mes?: string };

export const Route = createFileRoute("/app/inicio")({
  validateSearch: (s: Record<string, unknown>): Search => ({ mes: s.mes as string | undefined }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => ensurePage(context.queryClient, apiQuery("inicio", "page", deps)),
  head: () => pageHead("Início"),
  pendingComponent: Loading,
  component: InicioPage,
});

type Home = QueryOut<"inicio", "page">;
type DoctorHome = Extract<Home, { kind: "doctor" }>;

function InicioPage() {
  const search = Route.useSearch();
  const { data } = useApiSuspenseQuery("inicio", "page", search);
  if (data.kind === "reception") {
    return (
      <div className="mx-auto flex max-w-7xl flex-col gap-6">
        <BlocoAgendaHoje appointments={data.dayAppointments} />
        <RecepcaoBento {...data.bento} />
      </div>
    );
  }
  return <DoctorHomeView data={data} />;
}

/** A home do profissional, bloco a bloco, na ordem do perfil. */
function DoctorHomeView({ data }: { data: DoctorHome }) {
  function renderBloco(key: BlocoHomeKey): ReactNode {
    switch (key) {
      case "saudacao":
        return <BlocoSaudacao {...data.saudacao} />;
      case "agenda-hoje":
        return <BlocoAgendaHoje appointments={data.agendaHoje} />;
      case "pendencias":
        return <BlocoPendencias itens={data.pendencias} />;
      case "kpis":
        return data.kpiItens.length > 0 ? <BlocoKpis itens={data.kpiItens} /> : null;
      case "agenda-2-semanas":
        return <BlocoAgenda2Semanas {...data.agenda} linkHref={data.agenda.linkHref as StaticPath} />;
      case "mural":
        return <BlocoMural avisos={data.avisos} />;
      case "graficos":
        return data.graficos ? <BlocoGraficos {...data.graficos} /> : null;
      case "atalhos":
        return <BlocoAtalhos />;
      default:
        // Blocos das peças de especialidade: dados só chegam com a feature ligada.
        return renderBlocoEspecialidade(key, data.blocosEspecialidade[key]);
    }
  }
  return (
    <div
      className="mx-auto max-w-7xl"
      style={{ fontFamily: "var(--font-hanken), system-ui, sans-serif", color: "var(--color-ink)" }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: 14, alignItems: "flex-start" }}>
        {data.blocos.map(({ key, largura }) => {
          const node = renderBloco(key);
          if (!node) return null;
          return (
            <div
              key={key}
              style={{
                flex: largura === "cheia" ? "1 1 100%" : "1 1 calc(50% - 7px)",
                minWidth: largura === "meia" ? 320 : undefined,
                minHeight: 0,
              }}
            >
              {node}
            </div>
          );
        })}
      </div>
    </div>
  );
}
